import React, { useEffect, useMemo, useState } from 'react';
// Removed: import axios from 'axios';
import { api } from '../api';
import { EXPENSE_CATEGORIES, INVITATION_CATEGORY, joinGuests, VAT_EXCLUDED_CATEGORIES } from '../utils/expenseCategories';

export interface CreateExpenseUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  reimbursement_currency: string;
}

interface Props {
  isOpen: boolean;
  users: CreateExpenseUser[];
  onClose: () => void;
  onCreated: () => void;
}

// Match user's expense types and currency behavior
const currencies = ['EUR', 'USD', 'KES', 'NGN', 'ZAR', 'XOF', 'XAF', 'GHS', 'CHF', 'GBP', 'MAD', 'EGP', 'OTHER'];
const currencyNames: { [key: string]: string } = {
  EUR: 'Euro',
  USD: 'US Dollar',
  KES: 'Kenyan Shilling',
  NGN: 'Nigerian Naira',
  ZAR: 'South African Rand',
  XOF: 'West African CFA Franc',
  XAF: 'Central African CFA Franc',
  GHS: 'Ghanaian Cedi',
  CHF: 'Swiss Franc',
  GBP: 'British Pound',
  MAD: 'Moroccan Dirham',
  EGP: 'Egyptian Pound',
  OTHER: 'Other (specify)'
};

const AdminExpenseCreateModal: React.FC<Props> = ({ isOpen, users, onClose, onCreated }) => {
  const token = (typeof window !== 'undefined') ? localStorage.getItem('token') : '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [userId, setUserId] = useState<number | ''>('');
  const [date, setDate] = useState<string>('');
  const [category, setCategory] = useState<string>(EXPENSE_CATEGORIES[0].value);
  const [justification, setJustification] = useState('');
  const [guestNames, setGuestNames] = useState('');
  const isInvitation = category === INVITATION_CATEGORY;
  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>('EUR');
  const [showCustomCurrency, setShowCustomCurrency] = useState<boolean>(false);
  const [customCurrency, setCustomCurrency] = useState<string>('');
  const [currencyError, setCurrencyError] = useState<string>('');
  const [classification, setClassification] = useState<string>('');
  const [vatAmount, setVatAmount] = useState<string>('');
  const [showVatField, setShowVatField] = useState<boolean>(false);

  // Receipt & declaration
  const [receipt, setReceipt] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [noReceiptDeclaration, setNoReceiptDeclaration] = useState<boolean>(false);

  // FX states (auto computed like user form)
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [reimbursementAmount, setReimbursementAmount] = useState<number | null>(null);
  const [fetchedRate, setFetchedRate] = useState<number | null>(null);
  const [isLoadingRate, setIsLoadingRate] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    // reset form when opened
    setError(null);
    setLoading(false);
    setUserId('');
    setDate('');
    setCategory(EXPENSE_CATEGORIES[0].value);
    setJustification('');
    setGuestNames('');
    setAmount('');
    setCurrency('EUR');
    setCustomCurrency('');
    setShowCustomCurrency(false);
    setCurrencyError('');
    setClassification('');
    setVatAmount('');
    setShowVatField(false);
    setReceipt(null);
    setReceiptPreview(null);
    setNoReceiptDeclaration(false);
    setExchangeRate(null);
    setReimbursementAmount(null);
    setFetchedRate(null);
    setIsLoadingRate(false);
  }, [isOpen]);

  if (!isOpen) return null;

  // Date validation (12 months window, no future)
  const today = new Date().toISOString().split('T')[0];
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
  const minDate = twelveMonthsAgo.toISOString().split('T')[0];
  const validateDate = (value: string): string => {
    if (!value) return '';
    const selected = new Date(value);
    const todayDate = new Date(today);
    const minDateObj = new Date(minDate);
    if (selected > todayDate) return 'Future dates are not allowed.';
    if (selected < minDateObj) return 'Dates older than 12 months are not allowed.';
    return '';
  };
  const [dateError, setDateError] = useState('');
  useEffect(() => { setDateError(validateDate(date)); }, [date]);

  const validateCurrency = (code: string) => /^[A-Z]{3}$/.test(code);
  const getFinalCurrency = () => (currency === 'OTHER' ? customCurrency : currency);

  // Mirror VAT visibility logic from SubmitExpense
  useEffect(() => {
    // In SubmitExpense, VAT is shown when:
    // classification is 'support' or 'mix', and currency === 'EUR', and type not in excluded list
    const isApplicable = (classification === 'support' || classification === 'mix')
      && (currency === 'EUR')
      && !VAT_EXCLUDED_CATEGORIES.includes(category);
    setShowVatField(isApplicable);
    if (!isApplicable) {
      setVatAmount('');
    }
  }, [classification, currency, category]);

  const canSubmit = () => {
    if (!userId) return false;
    if (!date || dateError) return false;
    if (!justification.trim()) return false;
    if (isInvitation && !guestNames.trim()) return false;
    const amt = Number(amount);
    if (!isFinite(amt) || amt <= 0) return false;
    const finalCur = getFinalCurrency();
    if (!finalCur || !validateCurrency(finalCur)) return false;
    // Require receipt or no-receipt declaration
    if (!receipt && !noReceiptDeclaration) return false;
    // VAT must be numeric if shown
    if (showVatField && vatAmount !== '' && isNaN(Number(vatAmount))) return false;
    return true;
  };

  const handleCreate = async () => {
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        user_id: Number(userId),
        date,
        category,
        justification: isInvitation ? joinGuests(justification, guestNames) : justification.trim(),
        amount: Number(amount),
        currency: getFinalCurrency(),
        status: 'submitted',
        no_receipt: !!noReceiptDeclaration
      };

      if (classification) payload.classification = classification;
      if (vatAmount !== '') payload.vat_amount = Number(vatAmount);
      if (typeof exchangeRate === 'number') payload.exchange_rate = exchangeRate;
      if (typeof reimbursementAmount === 'number') payload.reimbursement_amount = reimbursementAmount;
      if (typeof fetchedRate === 'number') payload.fetched_exchange_rate = fetchedRate;
      if (receipt) {
        console.warn('Admin expense creation does not yet support file uploads; skipping receipt.');
      }

      await api.post('/api/admin/expenses', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onCreated();
      onClose();
    } catch (e: any) {
      console.error('Create expense failed', e);
      setError(e?.response?.data?.message || 'Failed to create expense');
    } finally {
      setLoading(false);
    }
  };

  const selectedUser = users.find(u => Number(u.id) === Number(userId));

  // Auto-fetch FX similar to user form when key inputs change
  useEffect(() => {
    const fetchRate = async () => {
      const finalCur = getFinalCurrency();
      const reimbCur = selectedUser?.reimbursement_currency;
      if (!userId || !amount || !finalCur || !date || !reimbCur) {
        setExchangeRate(null);
        setReimbursementAmount(null);
        setFetchedRate(null);
        return;
      }
      if (validateDate(date)) {
        setExchangeRate(null);
        setReimbursementAmount(null);
        setFetchedRate(null);
        return;
      }
      if (finalCur === reimbCur) {
        setExchangeRate(1.0);
        setFetchedRate(1.0);
        setReimbursementAmount(Math.round((Number(amount) * 1.0) * 100) / 100);
        return;
      }
      setIsLoadingRate(true);
      try {
        const resp = await api.get(`/api/exchange-rate?from=${finalCur}&to=${reimbCur}&date=${date}`);
        const rate = resp.data?.rate;
        if (typeof rate === 'number' && isFinite(rate) && rate > 0) {
          setFetchedRate(rate);
          setExchangeRate(rate);
          const calc = Math.round((Number(amount) * rate) * 100) / 100;
          setReimbursementAmount(calc);
        }
      } catch (e) {
        // ignore for now
      } finally {
        setIsLoadingRate(false);
      }
    };
    const t = setTimeout(fetchRate, 400);
    return () => clearTimeout(t);
  }, [userId, amount, currency, customCurrency, date, selectedUser?.reimbursement_currency]);

  return (
    <div className="admin-dialog-overlay">
      <div className="admin-dialog">
        <header className="admin-dialog__header">
          <h2 className="admin-dialog__title">Create Expense (on behalf)</h2>
          <button className="admin-dialog__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="admin-dialog__body">
          <div className="admin-dialog__columns admin-dialog__columns--stack">
            <section className="admin-dialog__section">
              <h3 className="admin-dialog__section-title">Basic Info</h3>
              <div className="admin-dialog__field-grid admin-dialog__field-grid--single">
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-create-user">User</label>
                  <select
                    id="admin-create-user"
                    className="admin-dialog__select"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Select user…</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
                    ))}
                  </select>
                </div>

                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-create-date">Date</label>
                  <input
                    id="admin-create-date"
                    type="date"
                    className="admin-dialog__input"
                    value={date}
                    min={minDate}
                    max={today}
                    onChange={(e) => setDate(e.target.value)}
                  />
                  {dateError && <div className="admin-dialog__error-text">{dateError}</div>}
                </div>

                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-create-category">Category</label>
                  <select
                    id="admin-create-category"
                    className="admin-dialog__select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-create-justification">Justification</label>
                  <textarea
                    id="admin-create-justification"
                    className="admin-dialog__textarea"
                    rows={3}
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                  />
                </div>

                {isInvitation && (
                  <div className="admin-dialog__field">
                    <label className="admin-dialog__label" htmlFor="admin-create-guests">Guest name(s) *</label>
                    <input
                      id="admin-create-guests"
                      className="admin-dialog__input"
                      value={guestNames}
                      onChange={(e) => setGuestNames(e.target.value)}
                      placeholder="e.g. Jane Doe (Acme), John Smith (Acme)"
                    />
                  </div>
                )}
              </div>
            </section>

            <section className="admin-dialog__section">
              <h3 className="admin-dialog__section-title">Classification & VAT</h3>
              <div className="admin-dialog__field-grid">
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-create-classification">Classification</label>
                  <select
                    id="admin-create-classification"
                    className="admin-dialog__select"
                    value={classification}
                    onChange={(e) => setClassification(e.target.value)}
                  >
                    {['', 'support', 'investment', 'mix'].map(opt => (
                      <option key={opt} value={opt}>{opt === '' ? '— Select —' : opt}</option>
                    ))}
                  </select>
                </div>
                {showVatField && (
                  <div className="admin-dialog__field">
                    <label className="admin-dialog__label" htmlFor="admin-create-vat">VAT Amount ({getFinalCurrency()})</label>
                    <input
                      id="admin-create-vat"
                      type="number"
                      className="admin-dialog__input"
                      value={vatAmount}
                      placeholder="0.00"
                      onChange={(e) => setVatAmount(e.target.value)}
                    />
                  </div>
                )}
              </div>
              {!showVatField && (
                <div className="admin-dialog__helper">VAT not applicable for current selection.</div>
              )}
            </section>

            <section className="admin-dialog__section">
              <div className="admin-dialog__section-header">
                <h3 className="admin-dialog__section-title">Original Amount</h3>
                <span className="admin-dialog__helper">Will reimburse in {selectedUser?.reimbursement_currency || '—'}</span>
              </div>
              <div className="admin-dialog__field-grid admin-dialog__field-grid--tight">
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-create-amount">Amount</label>
                  <input
                    id="admin-create-amount"
                    type="number"
                    className="admin-dialog__input"
                    value={amount}
                    placeholder="0.00"
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-create-currency">Currency</label>
                  <select
                    id="admin-create-currency"
                    className="admin-dialog__select"
                    value={currency}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCurrency(val);
                      setShowCustomCurrency(val === 'OTHER');
                      if (val !== 'OTHER') {
                        setCustomCurrency('');
                        setCurrencyError('');
                      }
                    }}
                  >
                    {currencies.map((c) => (
                      <option key={c} value={c}>{c} {currencyNames[c] ? `- ${currencyNames[c]}` : ''}</option>
                    ))}
                  </select>
                  {showCustomCurrency && (
                    <div className="admin-dialog__stack">
                      <input
                        className="admin-dialog__input"
                        value={customCurrency}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          setCustomCurrency(v);
                          if (v && !validateCurrency(v)) setCurrencyError('Please enter a valid 3-letter currency code (e.g., USD, EUR)');
                          else setCurrencyError('');
                        }}
                        placeholder="Enter 3-letter code"
                        maxLength={3}
                        style={{ textTransform: 'uppercase' as const }}
                      />
                      {currencyError && <div className="admin-dialog__error-text">{currencyError}</div>}
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-dialog__helper">
                {isLoadingRate
                  ? 'Fetching FX…'
                  : exchangeRate
                    ? `Fetched rate: ${exchangeRate.toFixed(4)} ${getFinalCurrency()}/${selectedUser?.reimbursement_currency}`
                    : 'Rate will populate after selecting user, date, amount, and currency.'}
              </div>
            </section>

            <section className="admin-dialog__section admin-dialog__section--white">
              <h3 className="admin-dialog__section-title">Auto FX & Reimbursement</h3>
              <div className="admin-dialog__helper">
                {selectedUser ? (
                  <div className="admin-dialog__stack">
                    <span>Fetched rate: <strong>{exchangeRate ? exchangeRate.toFixed(4) : '—'}</strong> {getFinalCurrency()}/{selectedUser.reimbursement_currency}</span>
                    <span>Reimbursement: <strong>{reimbursementAmount !== null ? reimbursementAmount.toFixed(2) : '—'}</strong> {selectedUser.reimbursement_currency}</span>
                  </div>
                ) : (
                  <span>Select a user to compute reimbursement in their currency.</span>
                )}
              </div>
            </section>

            <section className="admin-dialog__section admin-dialog__section--white">
              <h3 className="admin-dialog__section-title">Receipt</h3>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setReceipt(f);
                  if (f) {
                    if (f.type.startsWith('image/')) {
                      const reader = new FileReader();
                      reader.onload = () => setReceiptPreview(reader.result as string);
                      reader.readAsDataURL(f);
                    } else {
                      setReceiptPreview(null);
                    }
                  } else {
                    setReceiptPreview(null);
                  }
                }}
              />
              {receiptPreview ? (
                <img src={receiptPreview} alt="receipt preview" className="admin-dialog__receipt-preview" />
              ) : (
                receipt && <div className="admin-dialog__helper">{receipt.name}</div>
              )}
              <label className="admin-dialog__checkbox-row">
                <input type="checkbox" checked={noReceiptDeclaration} onChange={(e) => setNoReceiptDeclaration(e.target.checked)} />
                I declare I don't have a receipt for this expense
              </label>
            </section>
          </div>

          <footer className="admin-dialog__footer">
            <button className="admin-dialog__button admin-dialog__button--secondary" onClick={onClose}>Cancel</button>
            <button
              className="admin-dialog__button admin-dialog__button--primary"
              onClick={handleCreate}
              disabled={!canSubmit() || loading}
            >
              {loading ? 'Creating…' : 'Create expense'}
            </button>
          </footer>

          {error && <div className="admin-dialog__error-text">{error}</div>}
        </div>
      </div>
    </div>
  );
};

export default AdminExpenseCreateModal;
